import React, { PropsWithChildren, useEffect, useRef } from 'react';
import CloseButton from 'reusable-components/buttons/CloseButton';
import ActionDialogButton, {
  ActionDialogButtonProps,
} from 'reusable-components/dialogs/ActionDialog/ActionDialogButton';
import ActionDialogFigure, {
  ActionDialogFigureProps,
} from 'reusable-components/dialogs/ActionDialog/ActionDialogFigure';
import Overlay from 'reusable-components/dialogs/Overlay';

const CSS = require('./ActionDialog.module.scss');

export interface ActionDialogProps {
  title: string;
  icon?: ActionDialogFigureProps;
  text?: string;
  actions: ActionDialogButtonProps[];
  onClose: () => void;
  hideClose?: boolean;
}

function ActionDialog({
  title,
  icon,
  text,
  actions,
  onClose,
  hideClose,
  children,
}: PropsWithChildren<ActionDialogProps>) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogElement = dialogRef.current;
    if (dialogElement) {
      dialogElement.focus();
    }
  }, []);

  return (
    <Overlay portalId="actionModal" onEsc={onClose} containerSize="extraSmall">
      <dialog
        className={CSS.ActionDialog}
        ref={dialogRef}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        tabIndex={-1}
      >
        <header>
          {!hideClose && <CloseButton onClick={onClose} />}
          {icon && (
            <ActionDialogFigure variant={icon.variant}>
              {icon.children}
            </ActionDialogFigure>
          )}
        </header>
        <div className={CSS.ActionModalBody}>
          <h1 id="dialog-title" className={CSS.ActionTitle}>
            {title}
          </h1>
          {text && (
            <p id="dialog-description" className={CSS.ActionText}>
              {text}
            </p>
          )}
          {children}
        </div>
        <footer className={CSS.ActionFooter}>
          {actions
            .filter(Boolean)
            .map(({ children, ...buttonProps }, index) => (
              <ActionDialogButton
                key={`action-button-${index}`}
                {...buttonProps}
              >
                {children}
              </ActionDialogButton>
            ))}
        </footer>
      </dialog>
    </Overlay>
  );
}

export default ActionDialog;
